import { Controller } from '@hotwired/stimulus'

declare const EB: {
  Datepicker: { initDatePickers(minDate: Date): void }
};

declare global {
  interface Window {
    Rollbar?: { warning(message: string, context?: Record<string, unknown>): void };
  }
}

// Singleton loader: at most one date_picker <script> is ever inserted per page, and every
// connect() awaits the same promise, so closing/reopening the modal before onload fires can
// no longer evaluate pickadate/chrono twice (Rollbar #10594 family). Reset to null on failure
// so the next connect() retries the load.
let datePickerLoad: Promise<void> | null = null;
let datePickerLoadUrl: string | null = null;

function loadDatePickerScript(url: string): Promise<void> {
  if (datePickerLoad && datePickerLoadUrl === url) return datePickerLoad;

  datePickerLoadUrl = url;
  const existing = document.querySelector<HTMLScriptElement>('script[data-eb-date-picker]');
  datePickerLoad = new Promise<void>((resolve, reject) => {
    if (existing) {
      // Already injected by an earlier (possibly disconnected) controller instance: the fetch
      // that removal does not cancel is still in flight, so await its outcome instead of adding
      // a second script.
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('date picker script failed to load')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.dataset.ebDatePicker = 'true';
    script.src = url;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('date picker script failed to load'));
    document.head.appendChild(script);
  }).catch((error) => {
    datePickerLoad = null;
    datePickerLoadUrl = null;
    throw error;
  });

  return datePickerLoad;
}

export default class extends Controller {
  static targets = [
    'endDateInput',
    'startDateInput',
    'submitButton'
  ];

  static values = { datePickerUrl: String };

  declare endDateInputTarget: HTMLInputElement;
  declare startDateInputTarget: HTMLInputElement;
  declare submitButtonTarget: HTMLInputElement;
  declare readonly hasStartDateInputTarget: boolean;
  declare readonly datePickerUrlValue: string;

  connect(): void {
    // Submit handlers first: a date picker failure must not leave the modal open after a successful save.
    this.element.addEventListener('turbo:submit-start', () => {
      this.submitButtonTarget.disabled = true;
      this.submitButtonTarget.value = 'Saving...';
      this.submitButtonTarget.classList.add('btn--disabled');
    });

    this.element.addEventListener("turbo:submit-end", (event: CustomEvent<{ success: boolean }>) => {
      if (event.detail.success) {
        const closeLink = this.element.querySelector<HTMLElement>('[rel="modal:close"]');
        closeLink?.click();
        const closeTurboModal = document.querySelector<HTMLElement>('.turbo-modal-component__close-modal');
        closeTurboModal?.click();
        this.reload_location(event);
      } else {
        this.submitButtonTarget.disabled = false;
        this.submitButtonTarget.value = 'Save Vote Box';
        this.submitButtonTarget.classList.remove('btn--disabled');
      }
    });

    if (this.hasStartDateInputTarget) this.loadDatePicker();
  }

  private loadDatePicker(): void {
    if (typeof EB !== 'undefined' && typeof EB.Datepicker?.initDatePickers === 'function') {
      this.initializeDatePickers();
      return;
    }

    loadDatePickerScript(this.datePickerUrlValue)
      .then(() => {
        // The modal may have been closed (and this controller disconnected) before the shared
        // load promise settled; a disconnected controller must not initialize pickers.
        if (!this.element.isConnected) return;
        this.initializeDatePickers();
      })
      .catch(() => {
        console.error('[VoteBox::CreateForm] Date picker script failed to load');
        window.Rollbar?.warning('VoteBox date picker unavailable', { src: this.datePickerUrlValue, reason: 'load' });
      });
  }

  private initializeDatePickers(): void {
    try {
      EB.Datepicker.initDatePickers(new Date());
    } catch (error) {
      console.error('[VoteBox::CreateForm] Date picker initialization failed');
      window.Rollbar?.warning('VoteBox date picker unavailable', { src: this.datePickerUrlValue, reason: 'init' });
    }
  }

  public textIntoStartDateField(event) {
    event.preventDefault();
    this.startDateInputTarget.value = event.target.textContent
    this.startDateInputTarget.focus()
  }

  public textIntoEndDateField(event) {
    event.preventDefault();
    this.endDateInputTarget.value = event.target.textContent
    this.endDateInputTarget.focus()
  }

  private reload_location(_event) {
    location.reload();
  }
}
